import clsx from 'clsx';
import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';

import Icon from 'components/v3/Icon';
import {AppLanguage, persistLanguage, SUPPORTED_LANGUAGES} from 'i18n/detection';

/**
 * 语言切换器（i18n 四语言体系入口，见 CLAUDE.md 与设计文档「语言扩展：韩文/俄文」）。
 *
 * - LanguageSwitcher：桌面端，插在 TopNav CTA 区「登录」前。展开/收起与 NavDropdown
 *   同构走纯 CSS group-hover，另加 group-focus-within 保证键盘可达（Tab 聚焦即展开）。
 * - MobileLanguageSwitcher：移动端抽屉底部的分段切换按钮。
 *
 * 多语言独立 URL（2026-10）：语言由 URL 前缀唯一决定（无前缀=中文），切换 = 整页跳转到
 * 对应语言前缀的当前路径（保留 query），偏好仍写入 localStorage 作记忆。整页跳转换来
 * 架构确定性——URL 与内容语言永远一致（SEO 与分享场景的硬要求）。
 *
 * 选项文案用各语言的母语自称（「中文」「English」「한국어」「Русский」），刻意不走 t()——业界惯例。
 */

/** 切换到目标语言：改写当前 URL 的语言前缀段（跳过站点子路径首段），保留 query 与 hash */
const navigateToLanguage = (language: AppLanguage) => {
  persistLanguage(language);
  const {hash, pathname, search} = window.location;
  const segments = pathname.split('/').filter(Boolean);
  const second = segments[1];
  if (second === 'en' || second === 'ko' || second === 'ru') {
    segments.splice(1, 1);
  }
  if (language !== 'zh-CN') {
    segments.splice(1, 0, language);
  }
  window.location.assign(`/${segments.join('/') || ''}${search}${hash}`);
};

/** heroicons globe-alt，path 数据与 navData「支持语言」项一致 */
const GLOBE_PATHS = [
  'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418',
];

/** 语言代码 → 母语自称与短标签 */
const LANGUAGE_LABELS: Record<AppLanguage, {native: string; short: string}> = {
  en: {native: 'English', short: 'EN'},
  ko: {native: '한국어', short: 'KO'},
  ru: {native: 'Русский', short: 'RU'},
  'zh-CN': {native: '中文', short: '中'},
};

export const LanguageSwitcher: FC = () => {
  const {i18n, t} = useTranslation('common');
  const currentLabel = LANGUAGE_LABELS[i18n.language as AppLanguage] ?? LANGUAGE_LABELS['zh-CN'];

  return (
    <div className="relative group hidden sm:flex">
      <button
        aria-haspopup="listbox"
        aria-label={t('language.aria')}
        className="nav-link text-sm text-slate-600 hover:text-slate-900 font-medium flex items-center gap-1 py-2"
        type="button"
      >
        <Icon paths={GLOBE_PATHS} />
        <span>{currentLabel.short}</span>
        <svg
          className="w-3.5 h-3.5 transition-transform group-hover:rotate-180"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
        >
          <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="absolute top-full right-0 mt-2 w-40 glass-card nav-dropdown-panel rounded-lg p-1.5 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-300 transform origin-top shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
        {SUPPORTED_LANGUAGES.map((language) => (
          <button
            aria-selected={i18n.language === language}
            className={clsx(
              'w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
              i18n.language === language ? 'text-indigo-600 font-semibold' : 'text-slate-600 hover:bg-slate-50',
            )}
            key={language}
            onClick={() => navigateToLanguage(language)}
            role="option"
            type="button"
          >
            {LANGUAGE_LABELS[language].native}
          </button>
        ))}
      </div>
    </div>
  );
};

export const MobileLanguageSwitcher: FC = () => {
  const {i18n} = useTranslation('common');

  return (
    <div className="flex items-center gap-3">
      <span className="text-slate-400">
        <Icon paths={GLOBE_PATHS} />
      </span>
      <div className="flex flex-1 rounded-md overflow-hidden border border-slate-200">
        {SUPPORTED_LANGUAGES.map((language) => (
          <button
            className={clsx(
              'flex-1 py-2 text-xs font-medium transition-colors',
              i18n.language === language ? 'bg-slate-900 text-white' : 'text-slate-600 bg-slate-50',
            )}
            key={language}
            onClick={() => navigateToLanguage(language)}
            type="button"
          >
            {LANGUAGE_LABELS[language].native}
          </button>
        ))}
      </div>
    </div>
  );
};
